import {
  DEFAULT_CARRIER,
  ICreateShipment,
  IModifiedShipment,
} from '../models/shipment';
import type { IDuplicateFormInitial } from '../types/duplicate';
import {
  ICreateShipmentAddress,
  INITIAL_CreateShipmentAddress,
} from '@/models/address';

export type { IDuplicateFormInitial };

const transformAddress = (
  address: IModifiedShipment['recipient']['address'] | undefined
): ICreateShipmentAddress => {
  if (!address) return INITIAL_CreateShipmentAddress;

  return {
    firstName: address.firstName || '',
    lastName: address.lastName || '',
    company: address.company || '',
    displayName: address.displayName || '',
    street: address.street || '',
    street2: address.street2 || '',
    houseNumber: address.houseNumber || '',
    postalCode: address.postalCode || '',
    city: address.city || '',
    state: address.state || '',
    countryCode: address.countryCode || '',
    phoneNumber: address.phoneNumber || '',
  };
};

/**
 * Maps an IModifiedShipment to the initial state of the duplication form.
 *
 * Handled cases:
 * - One-way: no returnShipmentId or originalShipmentId → simple duplication, no return
 * - Round-trip: has a returnShipmentId → outbound + return with swapped addresses
 * - Return-only: has an originalShipmentId → one-way using return shipment data
 */
export const mapShipmentToCreateShipment = (
  sourceShipment: IModifiedShipment,
  originalServiceLevelId: string
): IDuplicateFormInitial => {
  const isRoundTrip = !!sourceShipment.returnShipmentId;
  const serviceLevel = originalServiceLevelId || '1';
  const partnerId =
    sourceShipment.partnerId || sourceShipment.sender?.accountId || '';
  const weightInGrams =
    sourceShipment.weightInGrams !== undefined
      ? String(sourceShipment.weightInGrams / 1000)
      : sourceShipment.labelWeightInGrams !== undefined
        ? String(sourceShipment.labelWeightInGrams / 1000)
        : '0';

  const shipment: ICreateShipment = {
    recipient: {
      accountId: sourceShipment.recipient?.accountId || '',
      address: transformAddress(sourceShipment.recipient?.address),
      notificationEmail: sourceShipment.recipient?.notificationEmail || '',
    },
    sender: {
      accountId: sourceShipment.sender?.accountId || '',
      address: transformAddress(sourceShipment.sender?.address),
      notificationEmail: sourceShipment.sender?.notificationEmail || '',
      addressId: sourceShipment.sender?.address?.originalAddressId || '',
    },
    serviceLevel,
    weightInGrams,
    partnerId,
  };

  const reference = sourceShipment.reference
    ? `[ASSISTANCE] ${sourceShipment.reference}`
    : '[ASSISTANCE]';

  const hasReturn = isRoundTrip;

  // Return addresses are swapped: return sender = outbound recipient, return recipient = outbound sender
  const returnShipment: ICreateShipment = {
    recipient: {
      accountId: sourceShipment.sender?.accountId || '',
      address: transformAddress(sourceShipment.sender?.address),
      notificationEmail: sourceShipment.sender?.notificationEmail || '',
    },
    sender: {
      accountId: sourceShipment.recipient?.accountId || '',
      address: transformAddress(sourceShipment.recipient?.address),
      notificationEmail: sourceShipment.recipient?.notificationEmail || '',
      addressId: '',
    },
    serviceLevel,
    weightInGrams,
    partnerId: sourceShipment.partnerId || '',
  };

  return {
    shipment,
    carrier: sourceShipment.carrierCode || DEFAULT_CARRIER,
    serviceLevel,
    partnerId,
    reference,
    carrierTrackingCode: sourceShipment.carrierTrackingCode || '',
    hasReturn,
    returnShipment,
    returnCarrier: sourceShipment.carrierCode || DEFAULT_CARRIER,
    returnReference: sourceShipment.returnShipment?.reference
      ? `[ASSISTANCE] ${sourceShipment.returnShipment.reference}`
      : reference,
    returnCarrierTrackingCode:
      sourceShipment.returnShipment?.carrierTrackingCode || '',
    boxId: sourceShipment.box?.id || '',
    boxThingName: sourceShipment.box?.thingName || '',
  };
};
